import type { LocationSnapshot, ShiftAggregate, ZonedMoment } from '@der-maschinfuehrer/contracts';

export const EXTREME_RECORDED_DISTANCE_METRES = 2_000_000;

export type SegmentWarningCode =
  | 'arrival_before_departure'
  | 'time_overlap'
  | 'non_continuation'
  | 'probable_duplicate'
  | 'negative_distance'
  | 'extreme_distance';

export interface SegmentWarning {
  readonly code: SegmentWarningCode;
  readonly message: string;
}

export interface SegmentWarningInput {
  readonly target_trip_segment_id: string;
  readonly actual_start: ZonedMoment | null;
  readonly actual_end: ZonedMoment | null;
  readonly origin: LocationSnapshot | null;
  readonly destination: LocationSnapshot | null;
  readonly train_or_service_number: string | null;
  readonly distance_metres: number | null;
}

/** Phase-4 warnings are advisory and deterministic; callers must not use them as a save gate. */
export function collectSegmentWarnings(
  aggregate: ShiftAggregate,
  input: SegmentWarningInput,
): readonly SegmentWarning[] {
  const warnings: SegmentWarning[] = [];
  const start = input.actual_start === null ? null : Date.parse(input.actual_start.instant);
  const end = input.actual_end === null ? null : Date.parse(input.actual_end.instant);

  if (start !== null && end !== null && end < start) {
    warnings.push({
      code: 'arrival_before_departure',
      message: 'Příjezd je dříve než odjezd. Zkontroluj datum, čas a UTC offset.',
    });
  }

  const liveSegments = aggregate.segments.filter((segment) => segment.metadata.tombstone === null);
  const targetIndex = liveSegments.findIndex(
    (segment) => segment.trip_segment_id === input.target_trip_segment_id,
  );
  const semanticIndex = targetIndex === -1 ? liveSegments.length : targetIndex;
  const previous = liveSegments[semanticIndex - 1];
  if (
    previous?.destination !== null &&
    previous?.destination !== undefined &&
    input.origin !== null &&
    previous.destination.location_id !== input.origin.location_id
  ) {
    warnings.push({
      code: 'non_continuation',
      message: `Úsek nenavazuje na předchozí cíl ${previous.destination.display_name}.`,
    });
  }

  const comparableNumber = input.train_or_service_number?.trim() || null;
  for (const segment of liveSegments) {
    if (segment.trip_segment_id === input.target_trip_segment_id) continue;
    const otherStart =
      segment.actual_start === null ? null : Date.parse(segment.actual_start.instant);
    const otherEnd = segment.actual_end === null ? null : Date.parse(segment.actual_end.instant);
    if (
      start !== null &&
      end !== null &&
      otherStart !== null &&
      otherEnd !== null &&
      start < otherEnd &&
      otherStart < end
    ) {
      if (!warnings.some((warning) => warning.code === 'time_overlap')) {
        warnings.push({
          code: 'time_overlap',
          message: 'Skutečný čas se překrývá s jiným úsekem této směny.',
        });
      }
    }
    const sameEndpoints =
      input.origin !== null &&
      input.destination !== null &&
      segment.origin?.location_id === input.origin.location_id &&
      segment.destination?.location_id === input.destination.location_id;
    const sameKnownNumber =
      comparableNumber !== null && segment.train_or_service_number?.trim() === comparableNumber;
    const sameKnownStart =
      input.actual_start !== null && segment.actual_start?.instant === input.actual_start.instant;
    if (sameEndpoints && (sameKnownNumber || sameKnownStart)) {
      if (!warnings.some((warning) => warning.code === 'probable_duplicate')) {
        warnings.push({
          code: 'probable_duplicate',
          message: 'Podobný úsek už v této směně existuje. Ověř, že nejde o duplicitu.',
        });
      }
    }
  }

  if (input.distance_metres !== null && input.distance_metres < 0) {
    warnings.push({
      code: 'negative_distance',
      message: 'Kilometráž je záporná. Hodnota zůstane zachovaná, ale zkontroluj ji.',
    });
  }
  if (
    input.distance_metres !== null &&
    Math.abs(input.distance_metres) > EXTREME_RECORDED_DISTANCE_METRES
  ) {
    warnings.push({
      code: 'extreme_distance',
      message: 'Kilometráž přesahuje 2 000 km. Hodnota zůstane zachovaná, ale zkontroluj ji.',
    });
  }
  return warnings;
}
